/**
 * Block User Hooks
 *
 * Provides:
 * - useBlockStatus: Query hook for directional block status
 * - useBlockUserMutation: Mutation to block a user
 * - useUnblockUserMutation: Mutation to unblock a user
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { apiFetch } from '../lib/api';
import { queryKeys } from '../lib/queryKeys';

type BlockStatus = 'viewer_blocked_target' | 'target_blocked_viewer' | 'none';

interface BlockStatusResponse {
  status: BlockStatus;
}

/**
 * Query hook for directional block status between viewer and target.
 *
 * Returns:
 * - 'viewer_blocked_target': Viewer blocked the target
 * - 'target_blocked_viewer': Target blocked the viewer
 * - 'none': No block exists
 */
export function useBlockStatus(targetUserId: string | null) {
  const { user, session } = useAuth();
  const viewerId = user?.id;
  const token = session?.access_token;

  return useQuery({
    queryKey: queryKeys.blocked.status(targetUserId ?? '', viewerId ?? ''),
    enabled: !!targetUserId && !!viewerId && !!token && targetUserId !== viewerId,
    queryFn: async ({ signal }) => {
      return apiFetch<BlockStatusResponse>(
        `/users/${targetUserId}/block-status`,
        { token, signal }
      );
    },
    staleTime: 30 * 1000, // 30 seconds
  });
}

/**
 * Mutation to block a user with optimistic updates.
 */
export function useBlockUserMutation() {
  const { user, session } = useAuth();
  const viewerId = user?.id;
  const token = session?.access_token;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (targetUserId: string) => {
      if (!token) throw new Error('Not authenticated');
      return apiFetch<{ success: boolean }>(`/users/${targetUserId}/block`, {
        token,
        method: 'POST',
      });
    },
    onMutate: async (targetUserId: string) => {
      if (!viewerId) return;

      // Optimistic: set status to viewer_blocked_target
      const statusKey = queryKeys.blocked.status(targetUserId, viewerId);
      await queryClient.cancelQueries({ queryKey: statusKey });
      const previousStatus = queryClient.getQueryData<BlockStatusResponse>(statusKey);

      queryClient.setQueryData<BlockStatusResponse>(statusKey, {
        status: 'viewer_blocked_target',
      });

      // Optimistic: strip blocked user from cached recent searches immediately
      const recentSearchesKey = queryKeys.user.recentSearches(viewerId);
      await queryClient.cancelQueries({ queryKey: recentSearchesKey });
      const previousRecentSearches = queryClient.getQueryData<Array<{ id: string; media_type: string }>>(recentSearchesKey);
      queryClient.setQueryData<Array<{ id: string; media_type: string }>>(recentSearchesKey, (old) => {
        if (!Array.isArray(old)) return old;
        return old.filter((item) => !(item.media_type === 'user' && String(item.id) === targetUserId));
      });

      return { previousStatus, statusKey, previousRecentSearches, recentSearchesKey };
    },
    onError: (_error, _targetUserId, context) => {
      // Rollback on error
      if (context?.previousStatus && context?.statusKey) {
        queryClient.setQueryData(context.statusKey, context.previousStatus);
      }
      if (context?.previousRecentSearches !== undefined && context?.recentSearchesKey) {
        queryClient.setQueryData(context.recentSearchesKey, context.previousRecentSearches);
      }
    },
    onSettled: (_data, _error, targetUserId) => {
      if (!viewerId) return;

      // Invalidate all related queries
      queryClient.invalidateQueries({ queryKey: queryKeys.blocked.status(targetUserId, viewerId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.blocked.listBase() });
      queryClient.invalidateQueries({ queryKey: queryKeys.blocked.count(viewerId), exact: true });
      queryClient.invalidateQueries({ queryKey: queryKeys.users.followStatus(targetUserId, viewerId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.users.followers(targetUserId, viewerId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.users.following(targetUserId, viewerId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.social.feedBase(viewerId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.user.recentSearches(viewerId) });
    },
  });
}

/**
 * Mutation to unblock a user with optimistic updates.
 */
export function useUnblockUserMutation() {
  const { user, session } = useAuth();
  const viewerId = user?.id;
  const token = session?.access_token;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (targetUserId: string) => {
      if (!token) throw new Error('Not authenticated');
      return apiFetch<{ success: boolean }>(`/users/${targetUserId}/block`, {
        token,
        method: 'DELETE',
      });
    },
    onMutate: async (targetUserId: string) => {
      if (!viewerId) return;

      // Optimistic: set status to none
      const statusKey = queryKeys.blocked.status(targetUserId, viewerId);
      await queryClient.cancelQueries({ queryKey: statusKey });
      const previousStatus = queryClient.getQueryData<BlockStatusResponse>(statusKey);

      queryClient.setQueryData<BlockStatusResponse>(statusKey, {
        status: 'none',
      });

      return { previousStatus, statusKey };
    },
    onError: (_error, _targetUserId, context) => {
      // Rollback on error
      if (context?.previousStatus && context?.statusKey) {
        queryClient.setQueryData(context.statusKey, context.previousStatus);
      }
    },
    onSettled: (_data, _error, targetUserId) => {
      if (!viewerId) return;

      // Invalidate all related queries
      queryClient.invalidateQueries({ queryKey: queryKeys.blocked.status(targetUserId, viewerId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.blocked.listBase() });
      queryClient.invalidateQueries({ queryKey: queryKeys.blocked.count(viewerId), exact: true });
      queryClient.invalidateQueries({ queryKey: queryKeys.users.followStatus(targetUserId, viewerId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.users.followers(targetUserId, viewerId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.users.following(targetUserId, viewerId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.social.feedBase(viewerId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.user.recentSearches(viewerId) });
    },
  });
}
